import {makeAutoObservable, observable, runInAction} from 'mobx';
import StudioRepository from '../../../../../data/repositories/StudioRepository';
import {errorMessage} from '../../../errors';
import {TITLE_MAX} from '../../../limits';
import {isQueued} from '../../../queue';
import {ACTIVE, isExporting, waitingStage} from '../../../store/jobStatus';
import {backoff, pollAfter, POLL_WAIT_MS} from '../../../store/polling';
import type {Backoff} from '../../../store/polling';
import {studioRequest} from '../../../store/studioRequests';
import type {JobSummary} from '../../../types';

/** How often the list refreshes while a microreel is being drafted or generated. */
export const ACTIVE_POLL_MS = 1500;
/** How often it refreshes otherwise, so jobs started elsewhere appear. */
export const IDLE_POLL_MS = 6000;
/** How soon a list that failed to load is asked for again. */
export const RETRY_POLL_MS = 5000;

/** Studio is drafting, checking or generating the microreel, so it cannot change now. */
export const isJobRunning = (job: JobSummary) => ACTIVE.has(job.status);

/**
 * When to refresh the list again: every 1.5 s while a microreel is drafted or generated, but
 * from 2 s up to 10 s while every running one only waits (in a queue, or while its MP4s
 * export) and nothing changes, and every 6 s otherwise.
 */
export function nextListPoll(
  jobs: JobSummary[],
  previous: Backoff | null,
): Backoff {
  const running = jobs.filter(isJobRunning);
  if (!running.length) return {key: 'idle', delay: IDLE_POLL_MS};
  if (
    running.every(job => waitingStage(job) || isExporting(job) || isQueued(job))
  )
    return backoff(
      previous,
      running
        .map(
          job =>
            `${job.id}:${job.status}:${job.stage}:${job.queue?.ahead ?? ''}`,
        )
        .join('|'),
      POLL_WAIT_MS,
    );
  return {key: 'active', delay: ACTIVE_POLL_MS};
}

export interface RenameDraft {
  id: string;
  title: string;
}

/**
 * The organization's Studio microreels, refreshed while the library is open, and the
 * rename and delete actions on them.
 */
class StudioLibraryStore {
  /** null until the first list arrives. */
  public jobs: JobSummary[] | null = null;
  /** Why the list could not load; the store keeps asking for it. */
  public error: string | null = null;
  /** Why the last rename or delete failed. */
  public actionError: string | null = null;
  /** The card being renamed and the name typed so far. */
  public renaming: RenameDraft | null = null;
  /** The microreel a rename or delete is being saved for. */
  public busyId: string | null = null;
  /** The microreel waiting for the author to confirm its deletion. */
  public toDelete: JobSummary | null = null;

  private repository = new StudioRepository();
  private organizationId?: string;
  /** Bumped when the library opens or closes: answers for an earlier opening are dropped. */
  private session = 0;
  /** Cancels the next refresh. */
  private cancelPoll?: () => void;
  private pace: Backoff | null = null;

  constructor() {
    // The list, the draft name and the job to delete are replaced, never changed in place,
    // so the page sees plain objects.
    makeAutoObservable<
      StudioLibraryStore,
      'repository' | 'organizationId' | 'session' | 'cancelPoll' | 'pace'
    >(this, {
      jobs: observable.ref,
      renaming: observable.ref,
      toDelete: observable.ref,
      repository: false,
      organizationId: false,
      session: false,
      cancelPoll: false,
      pace: false,
    });
  }

  /** What the page's alert says: a failed action outranks a failed refresh. */
  public get alert() {
    return this.actionError || this.error;
  }

  public get isLoading() {
    return !this.jobs && !this.error;
  }

  /** Starts listing the organization's microreels, and keeps the list current until `close`. */
  public open(organizationId?: string) {
    this.close();
    this.organizationId = organizationId;
    this.load(this.session);
  }

  /**
   * Stops refreshing and forgets this visit, so the next one starts at loading rather than
   * showing this list or an open dialog; answers still on their way are ignored.
   */
  public close() {
    this.session += 1;
    this.cancelPoll?.();
    this.cancelPoll = undefined;
    this.pace = null;
    this.jobs = null;
    this.error = null;
    this.actionError = null;
    this.renaming = null;
    this.busyId = null;
    this.toDelete = null;
  }

  public startRename(job: JobSummary) {
    this.renaming = {id: job.id, title: job.title.slice(0, TITLE_MAX)};
  }

  /**
   * Takes the typed name as a field with a maxLength would: a full name takes no more letters,
   * and a longer paste is cut to the limit.
   */
  public setRenameTitle(title: string) {
    if (!this.renaming) return;
    if (title.length > TITLE_MAX && this.renaming.title.length >= TITLE_MAX)
      return;
    this.renaming = {...this.renaming, title: title.slice(0, TITLE_MAX)};
  }

  public cancelRename() {
    this.renaming = null;
  }

  /** Saves the typed name; an empty or unchanged one just closes the form. */
  public async saveRename(job: JobSummary) {
    if (!this.renaming) return;
    const title = this.renaming.title.trim();
    if (!title || title === job.title) {
      this.renaming = null;
      return;
    }
    await this.change(job, async () => {
      const result = await studioRequest(this.organizationId, org =>
        this.repository.renameJob(org, job.id, title, job.storeVersion),
      );
      return () => {
        this.jobs = (this.jobs || []).map(item =>
          item.id === job.id
            ? {...item, title, storeVersion: result.job.storeVersion}
            : item,
        );
        this.renaming = null;
      };
    });
  }

  public requestDelete(job: JobSummary) {
    this.toDelete = job;
  }

  public cancelDelete() {
    this.toDelete = null;
  }

  /** Deletes the microreel the author confirmed. */
  public async confirmDelete() {
    const job = this.toDelete;
    this.toDelete = null;
    if (!job) return;
    await this.change(job, async () => {
      await studioRequest(this.organizationId, org =>
        this.repository.deleteJob(org, job.id, job.storeVersion),
      );
      return () => {
        this.jobs = (this.jobs || []).filter(item => item.id !== job.id);
      };
    });
  }

  private async load(session: number) {
    let delay = RETRY_POLL_MS;
    try {
      const list = await studioRequest(this.organizationId, org =>
        this.repository.listJobs(org),
      );
      if (session !== this.session) return;
      runInAction(() => {
        this.jobs = list;
        this.error = null;
      });
      this.pace = nextListPoll(list, this.pace);
      delay = this.pace.delay;
    } catch (e) {
      if (session !== this.session) return;
      runInAction(() => {
        this.error = errorMessage(e);
      });
    }
    // Not while the tab is hidden: it refreshes as soon as the tab shows again.
    this.cancelPoll = pollAfter(delay, () => this.load(session));
  }

  /**
   * Runs a rename or delete for `job`, marking it busy. `send` resolves to the change to make
   * to the list, which is dropped when the library closed meanwhile; a failure becomes
   * `actionError`.
   */
  private async change(job: JobSummary, send: () => Promise<() => void>) {
    const session = this.session;
    this.busyId = job.id;
    this.actionError = null;
    try {
      const apply = await send();
      if (session === this.session) runInAction(apply);
    } catch (e) {
      if (session === this.session)
        runInAction(() => {
          this.actionError = errorMessage(e);
        });
    } finally {
      if (session === this.session)
        runInAction(() => {
          this.busyId = null;
        });
    }
  }
}

export default StudioLibraryStore;
